import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listenToMyOrder, updateOrderStatus } from '../services/firebase';
import { useRider } from '../context/RiderContext';
import { useLanguage } from '../i18n/LanguageContext';
import { maskPhone, phoneTelHref } from '../utils/phone';
import { mapsDirectionsUrl, hasDeliveryCoords } from '../utils/maps';

export default function ActiveDelivery() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { currentOrder, setCurrentOrder, activeOrder } = useRider();
  const [orderData, setOrderData] = useState(currentOrder || activeOrder);
  const [loading, setLoading] = useState(false);
  const [codConfirmed, setCodConfirmed] = useState(false);
  const orderListener = useRef(null);

  const orderId = currentOrder?.id || activeOrder?.id;

  const STEPS = useMemo(() => [
    { status: 'confirmed', label: t('delivery.goToShop'), emoji: '🏪', desc: t('delivery.goToShopDesc'), action: t('delivery.pickedUp'), next: 'picked_up' },
    { status: 'ready', label: t('delivery.goToShop'), emoji: '🏪', desc: t('delivery.goToShopDesc'), action: t('delivery.pickedUp'), next: 'picked_up' },
    { status: 'picked_up', label: t('delivery.deliverCustomer'), emoji: '🛵', desc: t('delivery.deliverCustomerDesc'), action: t('delivery.markDelivered'), next: 'delivered' },
    { status: 'delivered', label: t('delivery.delivered'), emoji: '✅', desc: t('delivery.deliveredDesc'), action: null, next: null },
  ], [t]);

  useEffect(() => {
    if (!orderId) { navigate('/'); return; }
    if (!currentOrder && activeOrder) setCurrentOrder(activeOrder);
    orderListener.current = listenToMyOrder(orderId, (data) => {
      setOrderData(data);
      if (data.status !== 'picked_up') setCodConfirmed(false);
      if (data.status === 'cancelled') {
        toast(t('delivery.cancelled'));
        setCurrentOrder(null);
        navigate('/');
      }
    });
    return () => orderListener.current?.();
  }, [orderId, navigate, setCurrentOrder, activeOrder, currentOrder, t]);

  const currentStepIndex = STEPS.findIndex((s) => s.status === orderData?.status);
  const currentStep = STEPS[Math.max(0, currentStepIndex)];
  const isCash = orderData?.paymentMode === 'cash';
  const needsCodConfirm = orderData?.status === 'picked_up' && isCash && !codConfirmed;

  const handleNext = async () => {
    if (!orderId) return;
    if (orderData?.status === 'picked_up' && isCash && !codConfirmed) {
      toast.error(t('delivery.codRequired'));
      return;
    }
    setLoading(true);
    try {
      await updateOrderStatus(orderId, currentStep.next);
      if (currentStep.next === 'delivered') {
        toast.success(t('delivery.complete'));
        setCurrentOrder(null);
        setTimeout(() => navigate('/'), 2000);
      } else {
        toast.success(t('delivery.statusUpdated'));
      }
    } catch {
      toast.error(t('delivery.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const customerTel = phoneTelHref(orderData?.customerPhone);
  const showCustomerContact = orderData?.status === 'picked_up' && customerTel;
  const itemsSubtotal = orderData?.subtotal
    ?? orderData?.items?.reduce((sum, item) => sum + item.price * item.qty, 0)
    ?? 0;
  const deliveryFee = orderData?.deliveryFee ?? Math.max(0, (orderData?.total || 0) - itemsSubtotal);

  return (
    <div className="delivery-page">
      <div className="topbar">
        <div className="topbar-logo">My<span>Townly</span></div>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--green)', fontWeight: 700, letterSpacing: '0.04em' }}>
          📦 {t('delivery.active')}
        </span>
      </div>

      <div className="page-content" style={{ paddingBottom: 0 }}>
        <div className="delivery-status">
          <span className="delivery-status__emoji" aria-hidden>{currentStep.emoji}</span>
          <div>
            <div className="delivery-status__title">{currentStep.label}</div>
            <div className="delivery-status__desc">{currentStep.desc}</div>
          </div>
        </div>

        {orderData?.shopName && (orderData.status === 'confirmed' || orderData.status === 'ready') && (
          <div className="card delivery-section">
            <div className="delivery-section__label">{t('delivery.pickupFrom')}</div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>🏪 {orderData.shopName}</div>
            {orderData.shopAddress && (
              <div style={{ fontSize: 13, color: 'var(--gray-light)', marginTop: 6, lineHeight: 1.45 }}>
                {orderData.shopAddress}
              </div>
            )}
          </div>
        )}

        <div className="card delivery-section">
          <div className="delivery-section__label">{t('delivery.orderItems')}</div>
          {orderData?.items?.map((item, i) => (
            <div key={i} className="delivery-item-row">
              <span>{item.name} × {item.qty}</span>
              <span className="delivery-item-row__price">₹{item.price * item.qty}</span>
            </div>
          ))}
          <div className="delivery-summary-row">
            <span>{t('delivery.itemsSubtotal')}</span>
            <span>₹{itemsSubtotal}</span>
          </div>
          {deliveryFee > 0 && (
            <div className="delivery-summary-row">
              <span>{t('delivery.deliveryFee')}</span>
              <span>₹{deliveryFee}</span>
            </div>
          )}
          <div className="delivery-summary-row delivery-summary-row--total">
            <span>{isCash ? t('delivery.collectCod') : t('delivery.orderTotal')}</span>
            <span>₹{orderData?.total}</span>
          </div>
        </div>

        <div className="card delivery-section">
          <div className="delivery-section__label">{t('delivery.deliverTo')}</div>

          {(orderData?.customerName?.trim() || showCustomerContact) && (
            <div className="delivery-customer">
              <div className="delivery-customer__avatar" aria-hidden>👤</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {orderData?.customerName?.trim() && (
                  <div className="delivery-customer__name">{orderData.customerName.trim()}</div>
                )}
                {showCustomerContact && (
                  <div className="delivery-customer__phone">{maskPhone(orderData.customerPhone)}</div>
                )}
              </div>
            </div>
          )}

          <div className="delivery-address">📍 {orderData?.deliveryAddress}</div>

          {hasDeliveryCoords(orderData) && (
            <a
              href={mapsDirectionsUrl(orderData.deliveryLat, orderData.deliveryLng)}
              target="_blank"
              rel="noopener noreferrer"
              className="delivery-call-btn"
              style={{ marginTop: 10, background: 'var(--card)', color: 'var(--green)', border: '1px solid rgba(0,214,143,0.35)' }}
            >
              {t('delivery.navigate')}
            </a>
          )}

          {showCustomerContact && (
            <a href={customerTel} className="delivery-call-btn">
              {t('delivery.callCustomer')}
            </a>
          )}

          <div className={`delivery-payment ${isCash ? 'delivery-payment--cash' : 'delivery-payment--paid'}`}>
            {isCash
              ? t('delivery.collectCash', { total: orderData?.total })
              : t('delivery.paidOnline')}
          </div>
          {needsCodConfirm && (
            <button
              type="button"
              className="btn btn-outline"
              style={{ marginTop: 12, width: '100%' }}
              onClick={() => {
                setCodConfirmed(true);
                toast.success(t('delivery.confirmCod'));
              }}
            >
              {t('delivery.confirmCod')}
            </button>
          )}
          {orderData?.status === 'picked_up' && isCash && codConfirmed && (
            <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: 'var(--green)', textAlign: 'center' }}>
              ✓ {t('delivery.confirmCod')}
            </div>
          )}
        </div>
      </div>

      {currentStep.action && (
        <div className="delivery-footer">
          <button
            type="button"
            className="btn btn-green"
            onClick={needsCodConfirm ? () => toast.error(t('delivery.codRequired')) : handleNext}
            disabled={loading}
          >
            {loading ? <span className="spinner" /> : `✓ ${needsCodConfirm ? t('delivery.confirmCod') : currentStep.action}`}
          </button>
        </div>
      )}
    </div>
  );
}
